import React, { useMemo } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import { echartsContent } from '@op/utils';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import { DBO_V } from '@op/utils/format';
import { formatTimeToShow } from '@op/utils/time';
import { detailPageTips } from '@op/pages/Overview/KeyMetrics/utils';
import { dealWithOthersName } from '@op/utils/languageValue';
import { measureTextWidth } from '@op/pages/Overview/KeyMetrics/MentionsSentiment/Channel';
import { getChartPosition } from '@op/utils/chart';

interface ICommonBarChartProps {
  data: any;
  filter: any;
  height?: number;
  xAxisKey: string;
  metric: string;
  translateTags: (key: string) => string;
  formatMessage: (obj: { id: string; } | string) => string;
  metricOptions: Array<{ value: string; text: string; }>;
  attributeBy: string;
  extraProps?: Record<string, any>;
  defaultSelected?: any;
}

/**
 * 横向柱状图组件
 * - 支持数据自动排序（从大到小）
 * - 支持滚动条（数据量大时）
 * - Y轴显示标签名称
 * - attributeBy='total' 时使用 pieData 展示汇总柱状图
 */
const CommonBarChart: React.FC<ICommonBarChartProps> = ({
  data,
  filter,
  height = 366,
  xAxisKey,
  metric,
  translateTags,
  formatMessage,
  metricOptions,
  attributeBy,
  extraProps,
  defaultSelected
}) => {
  const isTotal = attributeBy === 'total';

  // 数据排序：计算每个类目的总和并排序（从大到小），仅非汇总模式使用
  const sortedData = useMemo(() => {
    const { series: seriesData, xAxis } = data || {};
    if (!xAxis || !seriesData) return { xAxis: [], seriesData: [] };

    // 计算每个类目的总值
    const categoryTotals = xAxis.map((_: string, index: number) => {
      const total = seriesData.reduce((sum: number, series: any) => {
        return sum + (series.data[index] || 0);
      }, 0);
      return { category: xAxis[index], total, index };
    });

    // 按总值从大到小排序
    categoryTotals.sort((a: any, b: any) => b.total - a.total);

    // 重新排列数据
    const sortedXAxis = categoryTotals.map((item: any) => item.category);
    const sortedSeriesData = seriesData.map((series: any) => ({
      ...series,
      data: categoryTotals.map((item: any) => series.data[item.index]),
    }));

    return { xAxis: sortedXAxis, seriesData: sortedSeriesData };
  }, [data]);

  const options = useMemo(() => {
    const { legendData, pieData, pieTotalData } = data || {};
    const { xAxis: sortedXAxis, seriesData: sortedSeriesData } = sortedData;

    // 汇总模式直接从 pieData 取 Y 轴类目
    const yAxisData = isTotal
      ? (pieData || []).map((item: any) => item.name)
      : sortedXAxis || [];

    // Y轴标签格式化：汇总模式用 translateTags，其他用 dealWithOthersName
    const yAxisLabelFormatter = isTotal
      ? (value: any) => translateTags(value)
      : (value: any) => dealWithOthersName(value, xAxisKey === 'language' ? 'language' : 'channel');

    // 计算 Y 轴标签的最大宽度，限制最大宽度避免标签溢出
    const MAX_LABEL_WIDTH = 200;

    // 最多显示 2 行，按单词换行，超出用 ... 截断
    // const yAxisLabelFormatter = (value: any) => {
    //   const MAX_LINES = 2;
    //   // 每行大约可容纳的字符数（12px 字体，英文约 7px/字符，中文约 12px/字符）
    //   const CHARS_PER_LINE = Math.floor(MAX_LABEL_WIDTH / 7);

    //   const label = String(yAxisLabelBase(value));
    //   if (label.length <= CHARS_PER_LINE) return label;

    //   const words = label.split(' ');
    //   const lines: string[] = [];
    //   let currentLine = '';

    //   for (const word of words) {
    //     const test = currentLine ? `${currentLine} ${word}` : word;
    //     if (test.length > CHARS_PER_LINE && currentLine) {
    //       lines.push(currentLine);
    //       if (lines.length >= MAX_LINES) break;
    //       currentLine = word;
    //     } else {
    //       currentLine = test;
    //     }
    //   }
    //   if (lines.length < MAX_LINES && currentLine) {
    //     lines.push(currentLine);
    //   }

    //   // 如果还有剩余文本未显示，最后一行截断加 ...
    //   const shown = lines.join(' ').length;
    //   if (shown < label.length) {
    //     const last = lines[lines.length - 1];
    //     lines[lines.length - 1] = last.length > CHARS_PER_LINE - 3
    //       ? last.slice(0, CHARS_PER_LINE - 3) + '...'
    //       : last + '...';
    //   }
    //   return lines.join('\n');
    // };

    const labelsForWidth = isTotal
      ? yAxisData.map((v: string) => translateTags(v))
      : sortedXAxis;
    const maxLabelWidth = Math.max(...(measureTextWidth(labelsForWidth, isTotal ? 'tag' : xAxisKey) || [])) || 60;
    const rounded = Math.round(maxLabelWidth / 10) * 10;
    const width = Math.min((rounded > maxLabelWidth ? rounded : rounded + 10) + 15, MAX_LABEL_WIDTH);

    // 判断是否需要显示滚动条（数据量超过 8 条）
    const needDataZoom = yAxisData.length > 8;

    // 通用柱状图基础配置
    const barBase = { type: 'bar' as const, barMaxWidth: 80 };

    // 处理 series 数据：汇总模式直接用 pieData，带独立颜色和右侧标签
    const seriesOptions = isTotal
      ? [{
        ...barBase,
        name: 'total',
        data: (pieData || []).map((item: any) => ({
          value: item.value ?? 0,
        })),
        label: {
          show: true,
          position: 'right' as const,
          formatter: (params: any) => DBO_V.reviews(params.value),
          fontSize: 12,
          color: '#838697',
        },
      }]
      : (sortedSeriesData || []).map((val: any) => ({
        ...barBase,
        name: val.key,
        data: val.data,
        stack: 'total',
        itemStyle: { color: val.color },
        label: {
          show: false,
          position: 'right' as const,
          formatter: (params: any) => DBO_V.reviews(params.value),
          fontSize: 12,
        },
      }));

    // tooltip 配置
    const valueText = metricOptions.find(option => option.value === metric)?.text || 'Value';
    const tooltip = isTotal
      ? {
        formatter: (params: any) =>
          formatChartParamsDate({
            className: 'dbo-tag-analysis-chart-tooltip',
            params,
            listTitleFunc: (item: any) => translateTags(item.name),
            tablesConfigFunc: (records: any) => {
              detailPageTips(records, formatMessage, extraProps?.detailPage);
              return [{
                showHeader: true,
                records,
                columns: [
                  { key: 'text', header: formatTimeToShow(filter) },
                  { key: 'value', align: 'right', weight: 'bold', header: formatMessage({ id: valueText }) },
                ],
              }];
            },
            valueFormat: (value: number) => {
              const percent = pieTotalData ? DBO_V.percentage(value, pieTotalData, 2) : '0%';
              return `${isNaN(value) ? '-' : DBO_V.reviews(value)}(${percent})`;
            },
          }),
      }
      : {
        enterable: true,
        confine: false,
        position: (
          pos: [any, any],
          params: any,
          dom: any,
          rect: any,
          size: { viewSize: [any, any]; },
        ) => getChartPosition(pos, params, dom, rect, size, 50, 12, 'bottom', 3),
        formatter: (params: any) =>
          formatChartParamsDate({
            className: 'dbo-tag-analysis-chart-tooltip',
            params,
            isSort: true,
            listTitleFunc: (item: any) => translateTags(item.seriesName),
            tablesConfigFunc: (records: any) => {
              detailPageTips(records, formatMessage, extraProps?.detailPage);
              return [{
                showHeader: true,
                records,
                columns: [
                  { key: 'text', header: formatTimeToShow(filter) },
                  { key: 'value', align: 'right', weight: 'bold', header: formatMessage({ id: valueText }) },
                ],
              }];
            },
            dealwithRecords: (records: any, total: number) =>
              records.map((item: any) => {
                if (item.originValue === 0) return item;
                return {
                  ...item,
                  value: `${item.value}(${DBO_V.percentage(item.originValue, total, 2)})`,
                };
              }),
          }),
      };

    return {
      tooltip,
      series: seriesOptions,
      xAxis: {
        type: 'value',
        boundaryGap: "0%",
        axisLabel: {
          formatter: (value: any) => echartsContent(value),
          color: '#838697',
          fontSize: 12,
        },
        axisLine: {
          show: true,
          lineStyle: { color: '#E5E5E5', type: 'solid' },
        },
        splitLine: {
          show: true,
          lineStyle: { color: '#E5E5E5', type: 'dashed' },
        },
      },
      yAxis: {
        type: 'category',
        position: 'left',
        data: yAxisData,
        inverse: true,
        axisLabel: {
          interval: 0,
          show: true,
          width: MAX_LABEL_WIDTH,
          overflow: 'truncate',
          ellipsis: '...',
          formatter: yAxisLabelFormatter,
        },
        splitLine: {
          show: false,
          lineStyle: { color: '#E5E5E5', type: 'dashed' },
        },
        axisLine: {
          show: true,
          lineStyle: { color: '#E5E5E5', type: 'solid' },
        },
        axisTick: { show: false },
      },
      ...(needDataZoom ? {
        dataZoom: [
          {
            type: 'inside', // 内置型 dataZoom，支持鼠标滚轮缩放
            yAxisIndex: [0, 1],
            filterMode: 'none',
            maxValueSpan: 9,
            minValueSpan: 9,
            startValue: 0,
            endValue: 9,
            moveOnMouseWheel: true,
            zoomOnMouseWheel: false,
          },
          {
            type: 'slider',
            filterMode: 'none',
            show: true,
            yAxisIndex: [0, 1],
            width: 0,
            maxValueSpan: 9,
            minValueSpan: 9,
            startValue: 0,
            endValue: 100,
            right: 10,
            showDetail: false,
            showDataShadow: false,
            moveHandleSize: 8,
            moveHandleIcon: 'none',
            zoomLock: true,
            borderColor: 'rgba(0,0,0,0)',
            moveHandleStyle: { color: '#D8DAE4', borderCap: 'square' },
            emphasis: { moveHandleStyle: { color: '#D8DAE4' } },
          },
        ],
      } : {}),
      grid: {
        right: '8%',
        bottom: isTotal ? 30 : 50,
        left: width,
        top: 10,
      },
      legend: isTotal
        ? { show: false }
        : {
          data: legendData || [],
          formatter: (params: any) => translateTags(params),
          ...(defaultSelected ? { selected: defaultSelected } : {}),
        },
    };
  }, [data, sortedData, isTotal, filter, xAxisKey, metric, translateTags, formatMessage, metricOptions]);

  return (
    <EChartCommon
      optionsProps={{ type: 'bar', excludeOptions: ['grid'] }}
      height={height}
      options={options}
      {...extraProps}
    />
  );
};

export default CommonBarChart;
